/**
 * GrowthPrequal — iter109 · 6 décisions distinctes post-préqualification
 *
 * **Brief CEO §1-§6** :
 *  QUALIFIÉ | VIVIER ⭐ | RAPPEL | INJOIGNABLE | ÉCARTÉ | REFUSÉ
 *  Ces statuts ne doivent JAMAIS être mélangés.
 *
 * Chaque décision :
 *   - Appelle un endpoint dédié `/growth/candidates/{id}/decision-{type}`
 *   - Enregistre une ligne dans `growth_decisions` (audit trail immuable)
 *   - Retire le candidat de la file (temporairement pour RAPPEL/INJOIGNABLE,
 *     définitivement pour QUALIFIÉ/VIVIER/ÉCARTÉ/REFUSÉ)
 *   - Passe automatiquement au candidat suivant
 *
 * Réutilisation stricte de la logique métier existante — pas de duplication :
 *   - QUALIFIÉ  → status=PREREC_DONE + outcome=qualified → Shortlist Direction
 *   - VIVIER ⭐ → in_talent_pool=True → /vivier (page Vivier existante)
 *   - RAPPEL   → growth_recall_at → revient auto à échéance (priorité 0)
 *   - INJOIGNABLE → call_attempts++, cooldown 4h ; après 3 tentatives auto-Vivier
 *   - ÉCARTÉ   → set_aside=True → /ecartes (page Écartés existante)
 *   - REFUSÉ   → status=rejected → /refuses (page Refusés existante)
 */
import { motion, AnimatePresence } from "framer-motion";
import {
    AlertCircle, ArrowLeft, ArrowRight, Archive, CalendarClock,
    CheckCircle2, ExternalLink, FileText, Loader2, PhoneCall, Save,
    Sparkles, Star, UserX, XCircle, Zap,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import CallButton from "../components/call/CallButton";
import { api } from "../lib/api";
import { useCallCenter } from "../lib/callCenter";
import { formatDate } from "../lib/format";


const CATEGORY_META = {
    top:         { emoji: "⭐", label: "ENTRETIEN DIRECTION — PRIORITAIRE", tone: "bg-yellow-50 text-yellow-800 border-yellow-300" },
    recommended: { emoji: "🟢", label: "ENTRETIEN RECOMMANDÉ",              tone: "bg-emerald-50 text-emerald-800 border-emerald-300" },
    reserve:     { emoji: "🟡", label: "SECOND AVIS / RÉSERVE",              tone: "bg-amber-50 text-amber-800 border-amber-300" },
    non_prio:    { emoji: "🔴", label: "NON PRIORITAIRE",                    tone: "bg-slate-100 text-slate-600 border-slate-300" },
};

const SUB_LABELS = {
    proactivite:     { label: "Proactivité démontrée",       cap: 25 },
    debrouillardise: { label: "Débrouillardise",             cap: 20 },
    raisonnement:    { label: "Qualité du raisonnement",     cap: 20 },
    communication:   { label: "Communication",               cap: 10 },
    ia_utilisation:  { label: "Usage IA / outils",           cap: 10 },
    motivation:      { label: "Motivation SHARITI",          cap: 10 },
    fiabilite:       { label: "Fiabilité / disponibilité",   cap: 5  },
};

const SESSION_KEY = "growth_prequal_session_history";
function loadHistory() {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "[]"); }
    catch { return []; }
}
function saveHistory(list) {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(list)); }
    catch {/* silencieux */}
}


export default function GrowthPrequal() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { lastCall } = useCallCenter();

    const [script, setScript] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState("");
    const [selectedCallId, setSelectedCallId] = useState("");
    const [candidateCalls, setCandidateCalls] = useState([]);

    const [queueSize, setQueueSize] = useState(0);
    const [analytics, setAnalytics] = useState(null);
    const [decisionRunning, setDecisionRunning] = useState(null);
    const [modal, setModal] = useState(null);  // { key, reason, note, date, reasons: [] }

    // Catalogs
    const [rejectReasons, setRejectReasons] = useState([]);
    const [setAsideReasons, setSetAsideReasons] = useState([]);
    const [talentPoolReasons, setTalentPoolReasons] = useState([]);

    const [history, setHistory] = useState(loadHistory);
    const historyIndex = history.indexOf(id);
    // iter109b fix · CEO 2026-09-12 · Le compteur position doit refléter la
    // taille RÉELLE de la file (queueSize inclut le candidat courant tant qu'il
    // n'a pas été traité). Anciennement on affichait "X / history.length" ce qui
    // donnait "1 / 1" au démarrage — trompeur alors que 113 candidats restent.
    // Nouvelle formule : "Candidat X · N restants (Y traités)"
    const treatedCount = history.length - 1;  // tous ceux visités sauf le courant
    const currentPosition = historyIndex + 1;
    const positionLabel = `Candidat n°${currentPosition} · ${Math.max(0, queueSize - 1)} restants`;

    useEffect(() => {
        if (!id) return;
        setHistory((prev) => {
            if (prev.includes(id)) return prev;
            const next = [...prev, id];
            saveHistory(next);
            return next;
        });
    }, [id]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [scriptRes, callsRes, queueRes, analyticsRes] = await Promise.all([
                api.get(`/growth/candidates/${id}/prequal-script`),
                api.get(`/twilio/calls?candidate_id=${id}&limit=10`).catch(() => ({ data: [] })),
                api.get(`/growth/call-queue/next?limit=1`).catch(() => ({ data: { queue_size: 0 } })),
                api.get(`/growth/analytics`).catch(() => ({ data: null })),
            ]);
            setScript(scriptRes.data);
            setNotes(scriptRes.data?.candidate?.growth_prequal_notes || "");
            setCandidateCalls(callsRes.data || []);
            if (callsRes.data?.[0]?.id) setSelectedCallId(callsRes.data[0].id);
            setQueueSize(queueRes.data?.queue_size || 0);
            setAnalytics(analyticsRes.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible de charger le candidat");
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Load catalogs (une seule fois)
    useEffect(() => {
        api.get("/candidates/reject-reasons").then(({ data }) => setRejectReasons(data.reasons || []));
        api.get("/candidates/set-aside-reasons").then(({ data }) => setSetAsideReasons(data.reasons || []));
        api.get("/candidates/talent-pool-reasons").then(({ data }) => setTalentPoolReasons(data.reasons || []));
    }, []);

    useEffect(() => {
        if (lastCall?.candidate_id === id) setTimeout(() => load(), 5000);
    }, [lastCall, id, load]);

    const c = script?.candidate;
    const alreadyPrequal = c?.growth_prequal_score != null;
    const prequalMeta = c?.growth_prequal_category
        ? CATEGORY_META[c.growth_prequal_category] || CATEGORY_META.non_prio
        : null;

    const goToNext = useCallback(async () => {
        try {
            // iter109b fix · CEO 2026-09-12 · Naviguer linéairement dans la file :
            // on exclut le candidat courant ET tous ceux déjà visités dans la
            // session (history), sinon on tourne en boucle. Fetch limit=500
            // (= tout le pipeline) et prendre le premier non visité.
            const { data } = await api.get(`/growth/call-queue/next?limit=500`);
            const visited = new Set(history);
            const nextCandidate = (data?.next || []).find((c) => c.id !== id && !visited.has(c.id));
            if (nextCandidate?.id) {
                navigate(`/growth/${nextCandidate.id}/prequal`);
                return true;
            }
            toast.success("File d'appels terminée — bravo !", { duration: 6000 });
            navigate("/growth");
            return false;
        } catch (_e) {
            toast.error("Impossible de charger le candidat suivant");
            return false;
        }
    }, [id, history, navigate]);

    const goToPrevious = useCallback(() => {
        const idx = history.indexOf(id);
        if (idx > 0) navigate(`/growth/${history[idx - 1]}/prequal`);
    }, [history, id, navigate]);

    const canGoPrevious = historyIndex > 0;

    // ── Wrapper d'appel API commun
    const runDecision = async (key, endpoint, body, successMsg) => {
        setDecisionRunning(key);
        try {
            await api.post(endpoint, body);
            toast.success(successMsg);
            await goToNext();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible d'enregistrer");
        } finally {
            setDecisionRunning(null);
        }
    };

    // 1. QUALIFIÉ — direct (pas de modale)
    const decideQualified = () => runDecision(
        "qualified",
        `/growth/candidates/${id}/decision-qualified`,
        { notes: notes.trim() || null },
        "Candidat qualifié → Shortlist Direction",
    );

    // 2. VIVIER ⭐ — modale (motif obligatoire)
    const openVivierModal = () => setModal({
        key: "talent_pool", reason: "to_recontact_later", note: notes.trim(), reasons: talentPoolReasons,
    });

    // 3. RAPPEL — modale (date/heure obligatoire)
    const openRecallModal = () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        setModal({ key: "recall", date: d.toISOString().slice(0, 16), note: notes.trim() });
    };

    // 4. INJOIGNABLE — direct
    const decideUnavailable = () => runDecision(
        "unavailable",
        `/growth/candidates/${id}/decision-unavailable`,
        { note: notes.trim() || null },
        "Tentative infructueuse enregistrée (cooldown 4h)",
    );

    // 5. ÉCARTÉ — modale (motif obligatoire)
    const openSetAsideModal = () => setModal({
        key: "set_aside", reason: "", note: notes.trim(), reasons: setAsideReasons,
    });

    // 6. REFUSÉ — modale (motif obligatoire)
    const openRejectModal = () => setModal({
        key: "reject", reason: "", note: notes.trim(), reasons: rejectReasons,
    });

    // Sauvegarde modale
    const submitModal = async () => {
        if (!modal) return;
        setDecisionRunning(modal.key);
        try {
            if (modal.key === "talent_pool") {
                if (!modal.reason) { toast.error("Choisis un motif"); setDecisionRunning(null); return; }
                await api.post(`/growth/candidates/${id}/decision-talent-pool`, {
                    reason: modal.reason,
                    note: modal.note || null,
                });
                toast.success("Placé au Vivier ⭐ (page /vivier)");
            } else if (modal.key === "recall") {
                if (!modal.date) { toast.error("Choisis une date"); setDecisionRunning(null); return; }
                await api.post(`/growth/candidates/${id}/decision-recall`, {
                    when: new Date(modal.date).toISOString(),
                    note: modal.note || null,
                });
                toast.success(`Rappel programmé pour ${new Date(modal.date).toLocaleString("fr-FR")}`);
            } else if (modal.key === "set_aside") {
                if (!modal.reason) { toast.error("Choisis un motif"); setDecisionRunning(null); return; }
                await api.post(`/growth/candidates/${id}/decision-set-aside`, {
                    reason: modal.reason,
                    note: modal.note || null,
                });
                toast.success("Candidat écarté (page /ecartes)");
            } else if (modal.key === "reject") {
                if (!modal.reason) { toast.error("Choisis un motif"); setDecisionRunning(null); return; }
                await api.post(`/growth/candidates/${id}/decision-reject`, {
                    reason: modal.reason,
                    note: modal.note || null,
                });
                toast.success("Candidat refusé (page /refuses)");
            }
            setModal(null);
            await goToNext();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible d'enregistrer");
        } finally {
            setDecisionRunning(null);
        }
    };

    const openCV = () => {
        if (!c?.cv_url) { toast.error("Aucun CV attaché"); return; }
        window.open(c.cv_url, "_blank", "noopener,noreferrer");
    };

    // Sauvegarde sans passer au suivant : mark-qualified (endpoint legacy)
    const saveOnly = async () => {
        setDecisionRunning("save_only");
        try {
            await api.post(`/growth/candidates/${id}/mark-qualified`, {
                notes: notes.trim() || null,
                outcome: "qualified",
            });
            toast.success("Enregistré (sans passer au suivant)");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossible");
        } finally { setDecisionRunning(null); }
    };

    if (loading || !c) {
        return <div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>;
    }

    const anyRunning = decisionRunning !== null;

    return (
        <div className="max-w-5xl mx-auto px-4 py-6 space-y-5" data-testid="growth-prequal-page">

            {/* NAV BAR */}
            <div className="rounded-2xl bg-white border-2 border-shariti-orange/20 p-3 flex items-center justify-between gap-3 shadow-sm sticky top-2 z-20" data-testid="growth-prequal-navbar">
                <button onClick={goToPrevious} disabled={!canGoPrevious || anyRunning} data-testid="growth-prequal-prev-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border-2 border-slate-200 text-slate-600 hover:border-shariti-orange hover:text-shariti-orange disabled:opacity-30 disabled:cursor-not-allowed transition">
                    <ArrowLeft className="h-3.5 w-3.5" />Précédent
                </button>
                <div className="flex flex-col items-center text-center">
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500" data-testid="growth-prequal-position">{positionLabel}</div>
                    {analytics && (
                        <div className="text-[10px] font-semibold text-slate-400" data-testid="growth-prequal-progress">
                            {analytics.total_callable || 0} à appeler · {analytics.prequal_progress?.done || 0} traités · <span className="text-shariti-orange font-black">{Math.max(0, (analytics.total_callable || 0) - (analytics.prequal_progress?.done || 0))} restants</span>
                            {analytics.recalls?.due_today > 0 && (
                                <span className="ml-2 text-blue-600 font-black">🔵 {analytics.recalls.due_today} rappels aujourd'hui</span>
                            )}
                            {analytics.recalls?.overdue > 0 && (
                                <span className="ml-2 text-red-600 font-black">⏰ {analytics.recalls.overdue} en retard</span>
                            )}
                        </div>
                    )}
                </div>
                <button onClick={goToNext} disabled={anyRunning} data-testid="growth-prequal-next-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border-2 border-slate-200 text-slate-600 hover:border-shariti-orange hover:text-shariti-orange disabled:opacity-30 disabled:cursor-not-allowed transition">
                    Suivant<ArrowRight className="h-3.5 w-3.5" />
                </button>
            </div>

            {/* HEADER */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <Link to="/growth" className="h-9 w-9 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 shrink-0" data-testid="growth-prequal-back-btn">
                        <ArrowLeft className="h-4 w-4" />
                    </Link>
                    <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 text-purple-700 border border-purple-200 text-[10px] font-black uppercase tracking-widest mb-1">
                            <Sparkles className="h-3 w-3" /> Préqualification Growth
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-2xl md:text-3xl font-black text-shariti-black truncate">{c.first_name} {c.last_name}</h1>
                            <button onClick={openCV} disabled={!c.cv_url} data-testid="growth-prequal-cv-btn"
                                title={c.cv_url ? "Ouvrir le CV" : "Aucun CV"}
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black border-2 transition ${
                                    c.cv_url
                                        ? "bg-shariti-orange text-white border-shariti-orange hover:bg-shariti-orange-hover shadow-md"
                                        : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                                }`}>
                                <FileText className="h-3.5 w-3.5" />Voir le CV
                                {c.cv_url && <ExternalLink className="h-3 w-3" />}
                            </button>
                        </div>
                        <p className="text-xs font-semibold text-slate-500 mt-0.5">
                            {c.layan_offer_title || "Business Development & Growth Associate"} · {c.phone || "Aucun numéro"}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="text-right">
                        <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Score dossier</div>
                        <div className="text-2xl font-black text-shariti-orange">{c.growth_score ?? "—"}/100</div>
                    </div>
                    <CallButton candidate={{ id: c.id, phone: c.phone, first_name: c.first_name, last_name: c.last_name }}
                        variant="wide" dataTestid="growth-prequal-call-btn" />
                </div>
            </div>

            {/* CONTEXTE */}
            <div className="rounded-2xl bg-white border-2 border-slate-100 p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Contexte dossier</div>
                <p className="text-sm text-slate-700 italic leading-relaxed">{c.growth_summary || "(aucun résumé)"}</p>
                {c.growth_a_verifier?.length > 0 && (
                    <div className="mt-3 bg-amber-50/60 border border-amber-100 rounded-xl p-3">
                        <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" /> Points à vérifier en priorité
                        </div>
                        <ul className="space-y-1">
                            {c.growth_a_verifier.map((v, i) => (<li key={i} className="text-[12px] text-amber-900 font-medium">? {v}</li>))}
                        </ul>
                    </div>
                )}
            </div>

            {/* SCRIPT */}
            <div className="rounded-2xl bg-white border-2 border-slate-100 p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-3">Script d'entretien · à suivre pendant l'appel</div>
                {script.personalized_questions?.length > 0 && (
                    <div className="mb-4">
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-2">Questions personnalisées (à partir du CV)</div>
                        <ol className="space-y-2">
                            {script.personalized_questions.map((q, i) => (
                                <li key={i} data-testid={`growth-perso-q-${i}`} className="pl-3 border-l-4 border-shariti-orange bg-shariti-cream/40 rounded-r-lg py-1.5 pr-2">
                                    <span className="text-[11px] font-black text-shariti-orange uppercase tracking-widest">Perso · Q{i + 1}</span>
                                    <p className="text-sm text-shariti-black font-semibold italic">« {q} »</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                )}
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">7 questions Growth communes</div>
                <ol className="space-y-2">
                    {script.common_questions.map((q, i) => (
                        <li key={q.key} data-testid={`growth-common-q-${q.key}`} className="border border-slate-100 rounded-xl p-3 hover:border-shariti-orange/40 transition">
                            <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Q{i + 1} · {q.label}</span>
                            <p className="text-sm text-shariti-black font-semibold mt-1">« {q.prompt} »</p>
                            <p className="text-[11px] text-slate-500 italic mt-1">🎯 {q.hint}</p>
                        </li>
                    ))}
                </ol>
            </div>

            {/* NOTES */}
            <div className="rounded-2xl bg-white border-2 border-slate-100 p-5">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Notes libres pendant / après l'appel</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                    data-testid="growth-prequal-notes" rows={5}
                    placeholder="Note ce qui t'a marqué : preuves concrètes, digressions, red flags, disponibilité, motivation authentique…"
                    className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-medium focus:border-shariti-orange focus:outline-none resize-none" />
                {candidateCalls.length > 0 && (
                    <div className="mt-3">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Appel enregistré (optionnel · pour l'IA)</div>
                        <select value={selectedCallId} onChange={(e) => setSelectedCallId(e.target.value)}
                            data-testid="growth-prequal-call-select"
                            className="w-full rounded-xl border-2 border-slate-200 px-3 py-2 text-sm font-semibold focus:border-shariti-orange focus:outline-none">
                            <option value="">— Aucun —</option>
                            {candidateCalls.map((call) => (
                                <option key={call.id} value={call.id}>Appel du {formatDate(call.started_at)} · {call.duration_sec || 0}s</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* 6 DÉCISIONS */}
            <div className="rounded-2xl bg-gradient-to-br from-shariti-orange/5 to-purple-500/5 border-2 border-shariti-orange/30 p-5" data-testid="growth-prequal-decision-bar">
                <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange mb-3 flex items-center gap-1.5">
                    <Zap className="h-3 w-3" /> Décision · sauvegarde et passe au candidat suivant
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <DecisionButton testid="growth-prequal-decision-qualified" icon={CheckCircle2} label="Qualifié"
                        color="bg-emerald-500 text-white border-emerald-600 hover:bg-emerald-600"
                        onClick={decideQualified} loading={decisionRunning === "qualified"} disabled={anyRunning} />
                    <DecisionButton testid="growth-prequal-decision-vivier" icon={Star} label="Vivier ⭐"
                        color="bg-purple-500 text-white border-purple-600 hover:bg-purple-600"
                        onClick={openVivierModal} loading={decisionRunning === "talent_pool"} disabled={anyRunning} />
                    <DecisionButton testid="growth-prequal-decision-recall" icon={CalendarClock} label="Rappel"
                        color="bg-blue-500 text-white border-blue-600 hover:bg-blue-600"
                        onClick={openRecallModal} loading={decisionRunning === "recall"} disabled={anyRunning} />
                    <DecisionButton testid="growth-prequal-decision-unavailable" icon={UserX} label="Injoignable"
                        color="bg-amber-500 text-white border-amber-600 hover:bg-amber-600"
                        onClick={decideUnavailable} loading={decisionRunning === "unavailable"} disabled={anyRunning} />
                    <DecisionButton testid="growth-prequal-decision-set-aside" icon={Archive} label="Écarté"
                        color="bg-slate-700 text-white border-slate-800 hover:bg-slate-800"
                        onClick={openSetAsideModal} loading={decisionRunning === "set_aside"} disabled={anyRunning} />
                    <DecisionButton testid="growth-prequal-decision-reject" icon={XCircle} label="Refusé"
                        color="bg-rose-500 text-white border-rose-600 hover:bg-rose-600"
                        onClick={openRejectModal} loading={decisionRunning === "reject"} disabled={anyRunning} />
                </div>

                <div className="mt-3 pt-3 border-t border-shariti-orange/20 flex items-center justify-end">
                    <button onClick={saveOnly} disabled={anyRunning} data-testid="growth-prequal-save-only-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-400 disabled:opacity-40 transition">
                        <Save className="h-3.5 w-3.5" />Enregistrer sans passer au suivant
                    </button>
                </div>
            </div>

            {/* RÉSULTAT IA */}
            {alreadyPrequal && prequalMeta && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl bg-white border-2 border-shariti-orange/40 p-5 shadow-md" data-testid="growth-prequal-result">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest border-2 ${prequalMeta.tone}`}>
                            {prequalMeta.emoji} {prequalMeta.label}
                        </span>
                        <div className="text-center">
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Préqualif</div>
                            <div className="text-2xl font-black text-blue-600">{c.growth_prequal_score}/100</div>
                        </div>
                    </div>
                    {c.growth_prequal_summary && (
                        <div className="mt-2 text-sm text-slate-700 italic leading-relaxed border-l-4 border-shariti-orange pl-3">{c.growth_prequal_summary}</div>
                    )}
                </motion.div>
            )}

            {/* MODALES */}
            <AnimatePresence>
                {modal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={() => !anyRunning && setModal(null)}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                        <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`growth-prequal-modal-${modal.key}`}
                            className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-5">
                            <h3 className="text-lg font-black text-shariti-black mb-3">
                                {modal.key === "talent_pool" && "Vivier ⭐ · profil à recontacter"}
                                {modal.key === "recall" && "Programmer un rappel"}
                                {modal.key === "set_aside" && "Écarter (pas de refus définitif)"}
                                {modal.key === "reject" && "Refuser définitivement"}
                            </h3>
                            <p className="text-xs text-slate-500 mb-4">
                                <b>{c.first_name} {c.last_name}</b> · score dossier {c.growth_score}
                            </p>

                            {(modal.key === "talent_pool" || modal.key === "set_aside" || modal.key === "reject") && (
                                <div className="mb-3">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                                        Motif <span className="text-rose-500">*</span>
                                    </label>
                                    <select value={modal.reason} onChange={(e) => setModal({ ...modal, reason: e.target.value })}
                                        data-testid={`growth-prequal-modal-reason-${modal.key}`}
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-shariti-orange">
                                        <option value="">— Sélectionne —</option>
                                        {modal.reasons.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                                    </select>
                                </div>
                            )}

                            {modal.key === "recall" && (
                                <div className="mb-3">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Rappeler à <span className="text-rose-500">*</span></label>
                                    <input type="datetime-local" value={modal.date}
                                        onChange={(e) => setModal({ ...modal, date: e.target.value })}
                                        data-testid="growth-prequal-modal-recall-date"
                                        className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-shariti-orange" />
                                    <p className="text-[10px] text-slate-400 mt-1">Le candidat réapparaîtra en TÊTE de la file à ce moment (priorité 0).</p>
                                </div>
                            )}

                            <div className="mb-4">
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Note (optionnelle)</label>
                                <textarea value={modal.note || ""} onChange={(e) => setModal({ ...modal, note: e.target.value })}
                                    rows={3} data-testid="growth-prequal-modal-note"
                                    placeholder="Détail utile pour la prochaine RH…"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-shariti-orange" />
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                                <button onClick={() => !anyRunning && setModal(null)} disabled={anyRunning}
                                    className="px-3 py-2 rounded-full text-xs font-black text-slate-600 hover:bg-slate-100 transition disabled:opacity-40">Annuler</button>
                                <button onClick={submitModal}
                                    disabled={anyRunning || ((modal.key === "recall") && !modal.date) || ((modal.key !== "recall") && !modal.reason)}
                                    data-testid="growth-prequal-modal-submit"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-black bg-shariti-orange text-white hover:bg-shariti-orange-hover disabled:opacity-50 transition">
                                    {anyRunning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                                    Confirmer et passer au suivant
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}


function DecisionButton({ icon: Icon, label, color, onClick, loading, disabled, testid }) {
    return (
        <button onClick={onClick} disabled={disabled} data-testid={testid}
            className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-black border-2 shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed ${color}`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {label}
        </button>
    );
}
